import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import {
  filterSlotsByClosedTimes,
  type ClosedTime,
} from "@/utils/bookingUtils";

export interface ExternalSlot {
  slot_date: string;
  times: string[];
  fetched_at: string;
}

export const useClinicExternalSlots = (clinicId: string | undefined) => {
  return useQuery({
    queryKey: ["clinic-external-slots", clinicId],
    queryFn: async () => {
      if (!clinicId) return null;

      const today = format(new Date(), "yyyy-MM-dd");

      const { data, error } = await supabase
        .from("clinic_external_slots")
        .select("slot_date, times, fetched_at")
        .eq("clinic_id", clinicId)
        .gte("slot_date", today)
        .order("slot_date", { ascending: true });

      if (error) {
        throw error;
      }

      return data as ExternalSlot[];
    },
    enabled: !!clinicId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

/**
 * Helper to get available times for a specific date from external slots.
 *
 * Pass the clinic's closed times: a partner's sync does not know about our
 * closures (Z-Point published 11:00-11:45 on a day we have it closed), and
 * the published times must never re-open a day the clinic is closed.
 */
export const getExternalSlotsForDate = (
  externalSlots: ExternalSlot[] | null | undefined,
  date: Date,
  clinicClosedTimes?: ClosedTime[],
): string[] | null => {
  if (!externalSlots || externalSlots.length === 0) {
    return null;
  }

  const dateStr = format(date, "yyyy-MM-dd");
  const slot = externalSlots.find((s) => s.slot_date === dateStr);

  if (slot) {
    return filterSlotsByClosedTimes(slot.times, date, clinicClosedTimes);
  }

  // No slot found for this date - fall back to opening_hours
  return null;
};

/**
 * Check if a clinic has external slots configured
 */
export const hasExternalSlots = (
  externalSlots: ExternalSlot[] | null | undefined,
): boolean => {
  return !!externalSlots && externalSlots.length > 0;
};
